// ===========================================
// The enquiry panel (enquiries plan §7, Phase 2), in the brief's order
// ===========================================
//   1. header: name, stage, temperature (when the school uses it), owner (a person, or
//      "Unassigned · <role or owner title>"), Ball with;
//   2. family details: contacts (click-to-call, "Log call"), children, availability, source, the
//      newsletter opt-in (read-only, "Set" opens the details), the existing family ("Link");
//   3. the original message;
//   4. the history (append-only) with "+ Call", "+ Note", "Family replied";
//   5. the Meet & Greet; 6. the closing details; 7. the next action ("Take it" when nobody has
//      it); 8. the reply box.
// Opening an enquiry changes nothing: every write is a button. Every action needs
// `enquiries.write`; booking or linking a Meet & Greet also `meet_and_greet.write`.

import { useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../../contexts/useAuth';
import { useEnquiry, useEnquiryConfig, useEnquiryMatches, useSetNextAction } from '../../../hooks/useEnquiries';
import { usePermissions } from '../../../hooks/usePermissions';
import { useOwnerTitle } from '../../../hooks/useOwnerTitle';
import { useSchoolTimezone } from '../../../contexts/TenantContext';
import { brandColors, meetAndGreetStatusColors, statusColors } from '../../../styles/brandColors';
import { formatInSchoolTz } from '../../../utils/dateUtils';
import type { Enquiry } from '../../../api/enquiry.api';
import SpamCheckChip, { ENQUIRY_SPAM_CHECK_TOOLTIP } from '../SpamCheckChip';
import EnquiryFlagChips from './EnquiryFlagChips';
import ConvertToMeetAndGreetDialog from '../ConvertToMeetAndGreetDialog';
import LinkMeetAndGreetDialog from './LinkMeetAndGreetDialog';
import MeetAndGreetNotesSection from '../../meetAndGreet/MeetAndGreetNotesSection';
import MoveStageDialog from './MoveStageDialog';
import NextActionDialog from './NextActionDialog';
import LogActivityDialog, { type LogKind } from './LogActivityDialog';
import FamilyRepliedDialog from './FamilyRepliedDialog';
import LinkFamilyDialog from './LinkFamilyDialog';
import EditEnquiryDetailsDialog from './EditEnquiryDetailsDialog';
import EnquiryHistory from './EnquiryHistory';
import EnquiryReplyBox from './EnquiryReplyBox';
import EnquiryOnboardingBlock from './EnquiryOnboardingBlock';
import {
  STAGE_COLORS,
  TEMPERATURE_COLORS,
  TEMPERATURE_LABELS,
  ballWithLabel,
  canMarkEnrolled,
  carriesBall,
  decodeMessage,
  dueState,
  formatDay,
  isArchived,
  isDeadBooking,
  ownerLabel,
  stageLabel,
  stageView,
  telHref,
} from './enquiryUi';

function CopyButton({ label, value }: { label: string; value: string }) {
  const { enqueueSnackbar } = useSnackbar();
  return (
    <Tooltip title={`Copy ${label}`}>
      <IconButton
        size="small"
        sx={{ color: 'text.secondary' }}
        aria-label={`Copy ${label}`}
        onClick={() => navigator.clipboard.writeText(value).then(
            () => enqueueSnackbar(`${label} copied to clipboard`, { variant: 'success' }),
            () => enqueueSnackbar(`Could not copy ${label}`, { variant: 'error' }),
          )
        }
      >
        <CopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

function Section({ title, action, children, testId }: { title: string; action?: ReactNode; children: ReactNode; testId: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid={testId}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </Paper>
  );
}

interface EnquiryPanelProps {
  enquiryId: string;
  onClose?: () => void;
}

export default function EnquiryPanel({ enquiryId, onClose }: EnquiryPanelProps) {
  const { can } = usePermissions();
  const { user } = useAuth();
  const canWrite = can('enquiries', 'write');
  // Booking or linking from an enquiry also MOVES the enquiry, so it needs both permissions, as
  // the server's create and link routes do (whole-phase review M1).
  const mayBookMng = canWrite && can('meet_and_greet', 'write');
  const timeZone = useSchoolTimezone();
  const ownerTitle = useOwnerTitle();

  const { data: enquiry, isLoading, isError } = useEnquiry(enquiryId);
  const { data: config } = useEnquiryConfig();
  // The existing family and other enquiries with this email or phone (warn only).
  const { data: matches } = useEnquiryMatches(
    { email: enquiry?.email, phone: enquiry?.phone, excludeEnquiryId: enquiryId },
    !!enquiry,
  );
  const takeIt = useSetNextAction();

  const [moveOpen, setMoveOpen] = useState(false);
  const [moveInitial, setMoveInitial] = useState<Enquiry['stage'] | undefined>(undefined);
  const [nextActionOpen, setNextActionOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [logKind, setLogKind] = useState<LogKind | null>(null);
  const [repliedOpen, setRepliedOpen] = useState(false);
  const [linkFamily, setLinkFamily] = useState<{ familyId: string; name: string } | null>(null);
  const [bookOpen, setBookOpen] = useState(false);
  const [linkMngOpen, setLinkMngOpen] = useState(false);

  const header = (
    <Box
      sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
      data-testid="enquiry-panel-header"
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
          {isLoading ? (
            <Skeleton variant="text" width={160} />
          ) : enquiry ? (
            <>
              <Typography variant="h6" fontWeight={600} sx={{ minWidth: 0, wordBreak: 'break-word' }}>
                {enquiry.firstName} {enquiry.lastName}
              </Typography>
              <Chip
                label={stageLabel(config, enquiry.stage)}
                size="small"
                sx={{ bgcolor: STAGE_COLORS[enquiry.stage].bg, color: STAGE_COLORS[enquiry.stage].text, fontWeight: 600 }}
              />
              {config?.settings.temperatureEnabled && enquiry.temperature && (
                <Chip
                  label={TEMPERATURE_LABELS[enquiry.temperature]}
                  size="small"
                  sx={{ bgcolor: TEMPERATURE_COLORS[enquiry.temperature].bg, color: TEMPERATURE_COLORS[enquiry.temperature].text }}
                />
              )}
              <SpamCheckChip spamCheck={enquiry.spamCheck} tooltip={ENQUIRY_SPAM_CHECK_TOOLTIP} />
              {isArchived(enquiry.archivedAt) && <Chip label="Archived" size="small" variant="outlined" />}
              <EnquiryFlagChips flags={enquiry.flags} />
            </>
          ) : (
            <Typography variant="h6">Enquiry</Typography>
          )}
        </Box>
        {onClose && (
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      {enquiry && (
        <Stack direction="row" spacing={2} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
          <Typography variant="body2" color="text.secondary">
            Owner: <strong>{ownerLabel(enquiry, ownerTitle).text}</strong>
          </Typography>
          {carriesBall(enquiry.stage) && (
            <Typography variant="body2" color="text.secondary">
              Ball with: <strong>{ballWithLabel(config, enquiry.ballWith)}</strong>
              {enquiry.ballWith === 'FAMILY' && enquiry.chaseDate ? ` (chase ${formatDay(enquiry.chaseDate)})` : ''}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );

  if (!enquiry) {
    return (
      <>
        {header}
        <Box sx={{ p: 3 }}>
          {isLoading && (
            <Stack spacing={2}>
              <Skeleton variant="rectangular" height={100} />
              <Skeleton variant="rectangular" height={80} />
              <Skeleton variant="rectangular" height={120} />
            </Stack>
          )}
          {isError && <Alert severity="error">Failed to load enquiry details.</Alert>}
        </Box>
      </>
    );
  }

  const view = stageView(config, enquiry.stage);
  const toReview = enquiry.stage === 'RESOLVED_TO_REVIEW';
  const owner = ownerLabel(enquiry, ownerTitle);
  const due = dueState(enquiry.nextActionDueDate, config?.schoolToday);
  const dueColor = due === 'OVERDUE' ? 'error.dark' : due === 'TODAY' ? '#9a3412' : 'text.primary';
  const otherEnquiries = Array.isArray(matches?.enquiries) ? matches.enquiries : [];
  const families = Array.isArray(matches?.families) ? matches.families : [];
  const mng = enquiry.MeetAndGreet;
  const mngColors = mng
    ? (meetAndGreetStatusColors as Record<string, { bg: string; text: string }>)[mng.status] ?? { bg: brandColors.coral, text: brandColors.textPrimary }
    : null;
  const meetAndGreetEnabled = config?.meetAndGreetEnabled !== false;

  const openMove = (initial?: Enquiry['stage']) => {
    setMoveInitial(initial);
    setMoveOpen(true);
  };

  return (
    <>
      {header}

      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        <Stack spacing={3}>
          {canWrite && toReview && (
            <Alert severity="info">This enquiry is waiting on the review screen. Choose its outcome there.</Alert>
          )}
          {canWrite && !toReview && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button variant="contained" size="small" onClick={() => openMove()}>
                Move stage
              </Button>
              {canMarkEnrolled(config, enquiry.stage) && (
                <Button variant="outlined" size="small" onClick={() => openMove('ENROLLED')}>
                  Mark enrolled
                </Button>
              )}
              <Button variant="outlined" size="small" onClick={() => setDetailsOpen(true)}>
                Edit details
              </Button>
            </Stack>
          )}

          {/* Phase 6: at Onboarding, the linked students' check-ins (null at any other stage). */}
          <EnquiryOnboardingBlock block={enquiry.onboarding} />

          {/* 2. Family details */}
          <Section title="Family" testId="enquiry-panel-family">
            {enquiry.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <EmailIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
                  {enquiry.email}
                </Typography>
                <CopyButton label="Email" value={enquiry.email} />
              </Box>
            )}
            {enquiry.phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <PhoneIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Link href={telHref(enquiry.phone)} variant="body2" sx={{ flex: 1, minWidth: 0 }} aria-label={`Call ${enquiry.phone}`}>
                  {enquiry.phone}
                </Link>
                <CopyButton label="Phone" value={enquiry.phone} />
                {canWrite && !toReview && (
                  <Button size="small" onClick={() => setLogKind('CALL')}>
                    Log call
                  </Button>
                )}
              </Box>
            )}

            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1.5 }} gutterBottom>
              Children
            </Typography>
            {enquiry.Children.length > 0 ? (
              <Stack spacing={0.5}>
                {enquiry.Children.map((c) => (
                  <Typography key={c.id} variant="body2">
                    <strong>{[c.firstName, c.lastName].filter(Boolean).join(' ') || 'Child'}</strong>
                    {c.age != null ? `, age ${c.age}` : ''}
                    {c.interest ? `, ${c.interest}` : ''}
                    {c.studentId ? ' (enrolled)' : ''}
                  </Typography>
                ))}
              </Stack>
            ) : enquiry.childFirstName || enquiry.instrument ? (
              <Typography variant="body2">
                <strong>{enquiry.childFirstName || 'Child'}</strong>
                {enquiry.instrument ? `, ${enquiry.instrument}` : ''}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                None listed
              </Typography>
            )}

            {enquiry.availability && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Availability: <strong>{enquiry.availability}</strong>
              </Typography>
            )}
            {enquiry.trialType && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Trial type: <strong>{enquiry.trialType}</strong>
              </Typography>
            )}
            <Typography variant="body2" sx={{ mt: 1 }}>
              Source: <strong>{enquiry.SourceOption?.label ?? 'Not asked'}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Received {formatDay(enquiry.receivedOn)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography variant="body2">
                Newsletter: <strong>{enquiry.newsletterOptIn ? 'Opted in' : 'Not opted in'}</strong>
              </Typography>
              {canWrite && !toReview && (
                <Button size="small" onClick={() => setDetailsOpen(true)}>
                  Set
                </Button>
              )}
            </Box>

            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap alignItems="center">
              {enquiry.Family ? (
                <Chip label={`Family: ${enquiry.Family.name}`} size="small" variant="outlined" />
              ) : (
                families.map((f) => (
                  <Box key={f.familyId} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }} data-testid="existing-family-chip">
                    <Chip
                      label={`Existing family: ${f.name}`}
                      size="small"
                      sx={{ bgcolor: brandColors.mint, color: brandColors.textPrimary }}
                    />
                    {canWrite && !toReview && (
                      <Button size="small" onClick={() => setLinkFamily(f)} aria-label={`Link to ${f.name}`}>
                        Link
                      </Button>
                    )}
                  </Box>
                ))
              )}
              {otherEnquiries.length > 0 && (
                <Tooltip
                  title={otherEnquiries
                    .map((o) => `${o.firstName} ${o.lastName}, ${stageLabel(config, o.stage)}${o.archived ? ' (archived)' : ''}, ${formatDay(o.receivedOn)}`)
                    .join('\n')}
                >
                  <Chip
                    label={`Possible duplicate (${otherEnquiries.length})`}
                    size="small"
                    // Dark text on the warning tint, never outlined coral on white (~1.8:1, #563; review L8).
                    sx={{ bgcolor: statusColors.warningBg, color: brandColors.textPrimary }}
                  />
                </Tooltip>
              )}
            </Stack>
          </Section>

          {/* 3. The original message */}
          <Section title={enquiry.subject ? `Subject: ${enquiry.subject}` : 'Message'} testId="enquiry-panel-message">
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
              {decodeMessage(enquiry.message)}
            </Typography>
          </Section>

          {/* 4. History */}
          <Box data-testid="enquiry-panel-history">
            <EnquiryHistory
              enquiryId={enquiry.id}
              config={config}
              timeZone={timeZone}
              onAdd={canWrite && !toReview ? (what) => (what === 'REPLIED' ? setRepliedOpen(true) : setLogKind(what)) : null}
            />
          </Box>

          {/* 5. Meet & Greet */}
          {meetAndGreetEnabled && (mng || mayBookMng) && (
            <Section title="Meet & Greet" testId="enquiry-panel-mng">
              {mng && mngColors ? (
                <Stack spacing={0.5}>
                  <Box>
                    <Chip
                      label={mng.status.replace(/_/g, ' ').toLowerCase()}
                      size="small"
                      sx={{ bgcolor: mngColors.bg, color: mngColors.text, fontWeight: 600, textTransform: 'capitalize' }}
                    />
                  </Box>
                  <Typography variant="body2">
                    {mng.scheduledDateTime
                      ? formatInSchoolTz(mng.scheduledDateTime, timeZone, { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
                      : 'No time set yet'}
                    {mng.Teacher ? ` · ${mng.Teacher.User.firstName} ${mng.Teacher.User.lastName}` : ''}
                  </Typography>
                  {/* The teacher's notes (#255): renders nothing, and fires no request, without
                      meet_and_greet.read (an enquiries.read viewer sees no note text). */}
                  {mng.status !== 'PENDING_VERIFICATION' && (
                    <Box sx={{ pt: 1 }}>
                      <MeetAndGreetNotesSection
                        meetAndGreetId={mng.id}
                        childName={enquiry.childFirstName || 'the child'}
                      />
                    </Box>
                  )}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  None booked
                </Typography>
              )}
              {mayBookMng && !toReview && (!mng || isDeadBooking(mng.status)) && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                  <Button size="small" variant="outlined" onClick={() => setBookOpen(true)}>
                    {mng ? 'Book a new Meet & Greet' : 'Book a Meet & Greet'}
                  </Button>
                  {/* The picker lists Meet & Greets, which is `meet_and_greet.read` (F-L4). */}
                  {can('meet_and_greet', 'read') && (
                    <Button size="small" variant="outlined" onClick={() => setLinkMngOpen(true)}>
                      {mng ? 'Link another' : 'Link an existing one'}
                    </Button>
                  )}
                </Stack>
              )}
            </Section>
          )}

          {/* 6. Closing */}
          {enquiry.stage === 'NOT_PROCEEDING' && (
            <Section title={stageLabel(config, 'NOT_PROCEEDING')} testId="enquiry-panel-closing">
              <Typography variant="body2">
                Reason: <strong>{enquiry.ClosedReasonOption?.label ?? 'Not recorded'}</strong>
              </Typography>
              {enquiry.closedReasonDetail && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {enquiry.closedReasonDetail}
                </Typography>
              )}
            </Section>
          )}
          {enquiry.stage === 'WAITLIST' && (enquiry.waitlistProgram || enquiry.waitlistPreferredDay) && (
            <Section title={stageLabel(config, 'WAITLIST')} testId="enquiry-panel-closing">
              {enquiry.waitlistProgram && (
                <Typography variant="body2">
                  Program: <strong>{enquiry.waitlistProgram}</strong>
                </Typography>
              )}
              {enquiry.waitlistPreferredDay && (
                <Typography variant="body2">
                  Preferred day: <strong>{enquiry.waitlistPreferredDay}</strong>
                </Typography>
              )}
            </Section>
          )}

          {/* 7. Next action */}
          {view?.isOwned && (
            <Section
              title="Next action"
              testId="enquiry-panel-next-action"
              action={
                canWrite ? (
                  <Stack direction="row" spacing={0.5}>
                    {owner.unassigned && user?.id && (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={takeIt.isPending}
                        onClick={() => takeIt.mutate({ id: enquiry.id, data: { ownerUserId: user.id } })}
                      >
                        Take it
                      </Button>
                    )}
                    <Button size="small" onClick={() => setNextActionOpen(true)}>
                      Edit
                    </Button>
                  </Stack>
                ) : undefined
              }
            >
              <Typography variant="body2">
                Owner: <strong>{owner.text}</strong>
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {enquiry.nextAction ? enquiry.nextAction : 'No next action set'}
              </Typography>
              <Typography variant="body2" sx={{ color: dueColor, fontWeight: due && due !== 'UPCOMING' ? 600 : 400 }}>
                Due:{' '}
                {enquiry.nextActionDueDate
                  ? `${formatDay(enquiry.nextActionDueDate)}${due === 'OVERDUE' ? ' (overdue)' : due === 'TODAY' ? ' (today)' : ''}`
                  : 'No due date'}
              </Typography>
            </Section>
          )}
        </Stack>
      </Box>

      {/* 8. Reply */}
      {canWrite && (
        <>
          <Divider />
          <EnquiryReplyBox key={enquiry.id} enquiry={enquiry} meetAndGreetEnabled={meetAndGreetEnabled} />
        </>
      )}

      {canWrite && (
        <>
          <MoveStageDialog enquiry={enquiry} open={moveOpen} onClose={() => setMoveOpen(false)} initialStage={moveInitial} />
          <NextActionDialog enquiry={enquiry} open={nextActionOpen} onClose={() => setNextActionOpen(false)} />
          <EditEnquiryDetailsDialog enquiry={enquiry} open={detailsOpen} onClose={() => setDetailsOpen(false)} />
          <LogActivityDialog enquiry={enquiry} kind={logKind ?? 'CALL'} open={logKind !== null} onClose={() => setLogKind(null)} />
          <FamilyRepliedDialog enquiry={enquiry} open={repliedOpen} onClose={() => setRepliedOpen(false)} />
          {linkFamily && <LinkFamilyDialog enquiry={enquiry} family={linkFamily} onClose={() => setLinkFamily(null)} />}
        </>
      )}
      {mayBookMng && bookOpen && (
        <ConvertToMeetAndGreetDialog open onClose={() => setBookOpen(false)} onSuccess={() => setBookOpen(false)} enquiry={enquiry} />
      )}
      {mayBookMng && linkMngOpen && <LinkMeetAndGreetDialog enquiry={enquiry} open onClose={() => setLinkMngOpen(false)} />}
    </>
  );
}
